import type { FC, ReactNode } from 'react'
import type {
    Activity,
    Avoid,
    Goal,
    MealKey,
    MealPlan,
    Pattern,
    PlannerInput,
    Sex
} from '@/lib/meal-planner'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    ArrowLeft,
    ArrowRight,
    Check,
    Clock,
    Flame,
    Info,
    Leaf,
    LoaderCircle,
    Printer,
    RefreshCw,
    RotateCcw,
    ShoppingBasket,
    TriangleAlert,
    Utensils,
    Wheat
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { printArea, removePrintFrames } from '@/lib/print-area'

import {
    SLOT_OF,
    UNDERWEIGHT_BMI,
    bmi,
    buildMealPlan,
    mealById,
    mealText,
    nextOption,
    shoppingList
} from '@/lib/meal-planner'

// Health questions: our own wording, not a validated screening form. Any yes
// keeps the plan at maintenance with no protein target; the eating disorder
// question also removes every number from the plan.
const HEALTH_QUESTIONS = [
    'Are you pregnant or breastfeeding?',
    'Do you live with diabetes, kidney disease, heart disease or another long-term condition?',
    'Has a doctor or dietitian given you a special diet, or told you that some foods affect your medicines?',
    'Have you had an eating disorder, or do you often feel very anxious or out of control around food or your weight?'
]
const EATING_QUESTION = 3

const ACTIVITY_OPTIONS: { value: Activity; label: string; hint: string }[] = [
    { value: 'low', label: 'Mostly sitting', hint: 'Desk job, little exercise' },
    { value: 'active', label: 'Active', hint: 'On your feet a lot, or exercise most days' },
    { value: 'very', label: 'Very active', hint: 'Physical job, or hard training most days' }
]

const GOAL_OPTIONS: { value: Goal; label: string; hint: string }[] = [
    { value: 'well', label: 'Eat healthier', hint: 'Keep your weight steady' },
    { value: 'lose', label: 'Lose weight gently', hint: 'About 500 calories a day less' },
    { value: 'gain', label: 'Gain weight or build muscle', hint: 'More protein and snacks' }
]

const PATTERN_OPTIONS: { value: Pattern; label: string }[] = [
    { value: 'all', label: 'I eat everything' },
    { value: 'pescatarian', label: 'Pescatarian' },
    { value: 'vegetarian', label: 'Vegetarian' },
    { value: 'vegan', label: 'Vegan' }
]

const AVOID_OPTIONS: { value: Avoid; label: string; hiddenFor: Pattern[] }[] = [
    { value: 'dairy', label: 'Dairy', hiddenFor: ['vegan'] },
    { value: 'gluten', label: 'Gluten', hiddenFor: [] },
    { value: 'egg', label: 'Eggs', hiddenFor: ['vegan'] },
    { value: 'nuts', label: 'Nuts and peanuts', hiddenFor: [] },
    { value: 'fish', label: 'Fish and shellfish', hiddenFor: ['vegetarian', 'vegan'] },
    { value: 'soy', label: 'Soy', hiddenFor: [] }
]

const SEX_OPTIONS: { value: Sex; label: string }[] = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
    { value: 'unspecified', label: 'Prefer not to say' }
]

const MEAL_LABEL: Record<MealKey, string> = {
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
    snack2: 'Snack'
}

const STEPS = 3
const LB_PER_KG = 2.20462
const CM_PER_INCH = 2.54

// Shown one after another while the plan is "prepared". The plan itself is
// ready instantly; the pause lets people see what went into it.
const PREP_STEPS = [
    'Checking your answers',
    'Working out your daily energy and protein',
    'Choosing meals that fit how you eat',
    'Writing your shopping list'
]
const PREP_STEP_MS = 700

// The plan and its answers are remembered in this browser only (localStorage),
// read after mount. Bump the version if the saved shape changes.
const STORE_KEY = 'wpf-meal-plan'
const STORE_VERSION = 1

interface FormState {
    answers: (boolean | null)[]
    age: string
    weight: string
    unit: 'kg' | 'lb'
    heightCm: string
    feet: string
    inches: string
    heightUnit: 'cm' | 'ft'
    sex: Sex
    activity: Activity
    goal: Goal
    pattern: Pattern
    avoid: Avoid[]
    snacks: number
    quick: boolean
}

interface SavedState {
    version: number
    form: FormState
    plan: MealPlan
}

const EMPTY_FORM: FormState = {
    answers: HEALTH_QUESTIONS.map(() => null),
    age: '',
    weight: '',
    unit: 'kg',
    heightCm: '',
    feet: '',
    inches: '',
    heightUnit: 'cm',
    sex: 'unspecified',
    activity: 'low',
    goal: 'well',
    pattern: 'all',
    avoid: [],
    snacks: 1,
    quick: false
}

const readSaved = (): SavedState | null => {
    try {
        const raw = window.localStorage.getItem(STORE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as SavedState
        if (parsed.version !== STORE_VERSION || !parsed.plan || !Array.isArray(parsed.plan.days)) return null
        if (!Array.isArray(parsed.form?.answers) || parsed.form.answers.length !== HEALTH_QUESTIONS.length) return null
        return parsed
    } catch {
        return null
    }
}

const writeSaved = (state: SavedState | null): void => {
    try {
        if (state) window.localStorage.setItem(STORE_KEY, JSON.stringify(state))
        else window.localStorage.removeItem(STORE_KEY)
    } catch {
        // Storage can be full or blocked; the plan still works for this visit.
    }
}

const choiceClass = (selected: boolean, disabled = false): string =>
    cn(
        'flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled
            ? 'cursor-not-allowed border-border bg-secondary/40 text-muted-foreground opacity-60'
            : selected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const inputClass =
    'h-12 w-full min-w-0 rounded-xl border border-border bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

const unitButtonClass = (selected: boolean): string =>
    cn(
        'px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        selected ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-secondary'
    )

const primaryButton =
    'inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50'

const secondaryButton =
    'inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

const Label: FC<{ children: ReactNode; htmlFor?: string; id?: string }> = ({ children, htmlFor, id }): ReactNode =>
    htmlFor ? (
        <label htmlFor={htmlFor} className='block text-sm font-semibold text-foreground'>{children}</label>
    ) : (
        <span id={id} className='block text-sm font-semibold text-foreground'>{children}</span>
    )

const formatNumber = (value: number): string => value.toLocaleString('en-US')

export const MealPlanner: FC = (): ReactNode => {
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [step, setStep] = useState(1)
    const [attempted, setAttempted] = useState(false)
    const [pending, setPending] = useState<MealPlan | null>(null)
    const [prepStep, setPrepStep] = useState(0)
    const [plan, setPlan] = useState<MealPlan | null>(null)
    const [dayIndex, setDayIndex] = useState(0)

    const headingRef = useRef<HTMLSpanElement>(null)
    const printAreaRef = useRef<HTMLDivElement>(null)
    const interacted = useRef(false)

    const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
        setForm((current) => ({ ...current, [key]: value }))
    }

    useEffect(() => removePrintFrames, [])

    // Bring back a plan saved on this device. After mount, so the server and
    // first client render match.
    useEffect(() => {
        const saved = readSaved()
        if (!saved) return
        setForm({ ...EMPTY_FORM, ...saved.form })
        setStep(3)
        setPlan(saved.plan)
    }, [])

    useEffect(() => {
        if (plan) writeSaved({ version: STORE_VERSION, form, plan })
        else if (interacted.current) writeSaved(null)
        // The answers are saved together with each plan change, so a swap keeps them too.
    }, [plan])

    useEffect(() => {
        if (interacted.current) headingRef.current?.focus()
    }, [step, plan])

    // Step through the "preparing" messages, then reveal the plan.
    useEffect(() => {
        if (!pending) return

        if (prepStep >= PREP_STEPS.length) {
            setPlan(pending)
            setPending(null)
            setDayIndex(0)
            return
        }

        const timer = window.setTimeout(() => setPrepStep((value) => value + 1), PREP_STEP_MS)
        return () => window.clearTimeout(timer)
    }, [pending, prepStep])

    const noNumbers = form.answers[EATING_QUESTION] === true
    const cautious = form.answers.some((answer) => answer === true)
    const healthDone = form.answers.every((answer) => answer !== null)

    const ageValue = Number(form.age)
    const weightValue = Number(form.weight)
    const weightKg = form.unit === 'kg' ? weightValue : weightValue / LB_PER_KG
    const ageValid = form.age !== '' && ageValue >= 18 && ageValue <= 100
    const underage = form.age !== '' && ageValue > 0 && ageValue < 18
    const weightValid = form.weight !== '' && weightKg >= 30 && weightKg <= 300
    const heightValue = form.heightUnit === 'cm'
        ? Number(form.heightCm)
        : (Number(form.feet) * 12 + Number(form.inches || 0)) * CM_PER_INCH
    const heightValid = (form.heightUnit === 'cm' ? form.heightCm !== '' : form.feet !== '') && heightValue >= 120 && heightValue <= 230
    const underweight = !noNumbers && weightValid && heightValid && bmi(weightKg, heightValue) < UNDERWEIGHT_BMI
    // With the eating disorder answer, only age is asked.
    const aboutYouValid = ageValid && (noNumbers || (weightValid && heightValid))

    // Weight loss is not offered with a health flag or a BMI under 18.5.
    const loseBlocked = cautious || underweight
    const goal: Goal = form.goal === 'lose' && loseBlocked ? 'well' : form.goal

    const go = (next: number): void => {
        interacted.current = true
        setAttempted(false)
        setStep(next)
    }

    const answer = (index: number, value: boolean): void => {
        setForm((current) => ({ ...current, answers: current.answers.map((item, i) => (i === index ? value : item)) }))
    }

    const toggleAvoid = (value: Avoid): void => {
        setForm((current) => ({
            ...current,
            avoid: current.avoid.includes(value) ? current.avoid.filter((item) => item !== value) : [...current.avoid, value]
        }))
    }

    const chooseGoal = (value: Goal): void => {
        setForm((current) => ({ ...current, goal: value, snacks: value === 'gain' ? Math.max(current.snacks, 2) : current.snacks }))
    }

    const visibleAvoid = AVOID_OPTIONS.filter((option) => !option.hiddenFor.includes(form.pattern))
    // Only the "leave out" choices that still apply to the chosen pattern.
    const avoid = form.avoid.filter((value) => visibleAvoid.some((option) => option.value === value))

    const plannerInput = (): PlannerInput => ({
        age: ageValue,
        weightKg,
        heightCm: heightValue,
        sex: form.sex,
        activity: form.activity,
        goal,
        pattern: form.pattern,
        avoid,
        snacks: form.snacks,
        quick: form.quick,
        cautious,
        noNumbers
    })

    const submit = (): void => {
        interacted.current = true
        setPrepStep(0)
        setPending(buildMealPlan(plannerInput()))
    }

    const restart = (): void => {
        interacted.current = true
        setPlan(null)
        setForm(EMPTY_FORM)
        setStep(1)
    }

    const swap = (day: number, key: MealKey): void => {
        if (!plan) return
        const entry = plan.days[day]
        const current = entry.meals[key]
        if (!current) return
        const taken = Object.entries(entry.meals).filter(([other]) => other !== key).map(([, id]) => id as string)
        const next = nextOption(current, SLOT_OF[key], { pattern: form.pattern, avoid, quick: form.quick }, taken)
        if (!next) return

        interacted.current = true
        setPlan({
            ...plan,
            days: plan.days.map((item, index) => (index === day ? { ...item, meals: { ...item.meals, [key]: next } } : item))
        })
    }

    const print = (): void => {
        if (printAreaRef.current) printArea(printAreaRef.current, 'Personalized Meal Plan')
    }

    const showForm = !plan && !pending
    const groups = plan ? shoppingList(plan.days, form.pattern, avoid) : []
    const targets = plan?.targets ?? null

    return (
        <section
            aria-label='Meal plan builder'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm print:border-0 print:shadow-none'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Utensils className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Meal plan builder
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Answer a few questions and get a 7-day meal plan with your daily targets and a shopping list.
                </span>
                {showForm && (
                    <span className='mt-3 flex flex-wrap gap-2'>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>{STEPS} short steps</span>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>About 2 minutes</span>
                        <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>Stays in your browser</span>
                    </span>
                )}
            </div>

            <div className='px-5 py-6 sm:px-6'>
                {showForm && (
                    <div>
                        <span className='text-sm font-semibold text-primary'>Step {step} of {STEPS}</span>
                        <div
                            className='mt-3 grid grid-cols-3 gap-1.5'
                            role='progressbar'
                            aria-valuemin={1}
                            aria-valuemax={STEPS}
                            aria-valuenow={step}
                            aria-valuetext={`Step ${step} of ${STEPS}`}
                            aria-label='Planner progress'
                        >
                            {[1, 2, 3].map((item) => (
                                <span
                                    key={item}
                                    className={cn('h-2 rounded-full', item < step ? 'bg-primary' : item === step ? 'bg-primary/40' : 'bg-secondary')}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {showForm && step === 1 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            Quick health check
                        </span>
                        <span className='mt-1 block text-sm text-muted-foreground'>
                            These questions decide how much the plan should change your eating, and when to check with a professional first.
                        </span>

                        <span className='mt-5 flex flex-col gap-4'>
                            {HEALTH_QUESTIONS.map((question, index) => (
                                <span key={question} className='block rounded-xl border border-border bg-background px-4 py-3'>
                                    <span id={`meal-health-${index}`} className='block text-sm leading-relaxed text-foreground'>{question}</span>
                                    <span role='group' aria-labelledby={`meal-health-${index}`} className='mt-3 flex gap-2'>
                                        {[true, false].map((value) => (
                                            <button
                                                key={String(value)}
                                                type='button'
                                                aria-pressed={form.answers[index] === value}
                                                onClick={() => answer(index, value)}
                                                className={chipClass(form.answers[index] === value)}
                                            >
                                                {value ? 'Yes' : 'No'}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            ))}
                        </span>

                        {noNumbers ? (
                            <span className='mt-5 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4 text-sm leading-relaxed text-foreground'>
                                <TriangleAlert className='mt-0.5 size-5 shrink-0 text-primary' />
                                <span>
                                    Thank you for telling us. Your plan will show regular, varied meals with no calories or
                                    numbers to track. If food or weight is on your mind a lot, a doctor or a registered
                                    dietitian can help, and you deserve that support.
                                </span>
                            </span>
                        ) : cautious && (
                            <span className='mt-5 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4 text-sm leading-relaxed text-foreground'>
                                <TriangleAlert className='mt-0.5 size-5 shrink-0 text-primary' />
                                <span>
                                    Please check with your doctor, midwife or a registered dietitian before you change how you
                                    eat. You can still build a plan to take to that conversation. It will not cut calories or
                                    set a protein target.
                                </span>
                            </span>
                        )}

                        {attempted && !healthDone && (
                            <span role='alert' className='mt-4 block text-sm font-medium text-destructive'>
                                Please answer all {HEALTH_QUESTIONS.length} questions.
                            </span>
                        )}

                        <span className='mt-6 flex justify-end'>
                            <button type='button' onClick={() => (healthDone ? go(2) : setAttempted(true))} className={primaryButton}>
                                Next: about you
                                <ArrowRight className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {showForm && step === 2 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            About you
                        </span>
                        <span className='mt-1 block text-sm text-muted-foreground'>
                            {noNumbers
                                ? 'Your plan has no numbers, so we only need your age, to check the planner suits you.'
                                : 'These are used to estimate how much energy and protein you need each day.'}
                        </span>

                        <span className='mt-5 grid gap-4 sm:grid-cols-2'>
                            <span className='block'>
                                <Label htmlFor='meal-age'>Age</Label>
                                <input
                                    id='meal-age'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    autoComplete='off'
                                    value={form.age}
                                    onChange={(event) => update('age', event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                    aria-invalid={attempted && !ageValid}
                                    className={cn(inputClass, 'mt-2')}
                                    placeholder='e.g. 42'
                                />
                            </span>
                            {!noNumbers && (<>
                            <span className='block'>
                                <Label htmlFor='meal-weight'>Weight</Label>
                                <span className='mt-2 flex gap-2'>
                                    <input
                                        id='meal-weight'
                                        inputMode='decimal'
                                        autoComplete='off'
                                        value={form.weight}
                                        onChange={(event) => update('weight', event.target.value.replace(/[^0-9.]/g, '').slice(0, 5))}
                                        aria-invalid={attempted && !weightValid}
                                        className={inputClass}
                                        placeholder={form.unit === 'kg' ? 'e.g. 70' : 'e.g. 155'}
                                    />
                                    <span role='group' aria-label='Weight unit' className='flex shrink-0 overflow-hidden rounded-xl border border-border'>
                                        {(['kg', 'lb'] as const).map((value) => (
                                            <button key={value} type='button' aria-pressed={form.unit === value} onClick={() => update('unit', value)} className={unitButtonClass(form.unit === value)}>
                                                {value}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            </span>
                            <span className='block'>
                                <Label htmlFor={form.heightUnit === 'cm' ? 'meal-height' : 'meal-feet'}>Height</Label>
                                <span className='mt-2 flex gap-2'>
                                    {form.heightUnit === 'cm' ? (
                                        <input
                                            id='meal-height'
                                            inputMode='numeric'
                                            pattern='[0-9]*'
                                            autoComplete='off'
                                            value={form.heightCm}
                                            onChange={(event) => update('heightCm', event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                            aria-invalid={attempted && !heightValid}
                                            className={inputClass}
                                            placeholder='e.g. 170'
                                        />
                                    ) : (
                                        <span className='flex w-full min-w-0 gap-2'>
                                            <input
                                                id='meal-feet'
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                autoComplete='off'
                                                aria-label='Feet'
                                                value={form.feet}
                                                onChange={(event) => update('feet', event.target.value.replace(/[^0-9]/g, '').slice(0, 1))}
                                                aria-invalid={attempted && !heightValid}
                                                className={inputClass}
                                                placeholder='ft'
                                            />
                                            <input
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                autoComplete='off'
                                                aria-label='Inches'
                                                value={form.inches}
                                                onChange={(event) => update('inches', event.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                                                className={inputClass}
                                                placeholder='in'
                                            />
                                        </span>
                                    )}
                                    <span role='group' aria-label='Height unit' className='flex shrink-0 overflow-hidden rounded-xl border border-border'>
                                        {(['cm', 'ft'] as const).map((value) => (
                                            <button key={value} type='button' aria-pressed={form.heightUnit === value} onClick={() => update('heightUnit', value)} className={unitButtonClass(form.heightUnit === value)}>
                                                {value === 'cm' ? 'cm' : 'ft/in'}
                                            </button>
                                        ))}
                                    </span>
                                </span>
                            </span>
                            <span className='block'>
                                <Label id='meal-sex'>Sex</Label>
                                <span role='group' aria-labelledby='meal-sex' className='mt-2 flex flex-wrap gap-2'>
                                    {SEX_OPTIONS.map((option) => (
                                        <button key={option.value} type='button' aria-pressed={form.sex === option.value} onClick={() => update('sex', option.value)} className={chipClass(form.sex === option.value)}>
                                            {option.label}
                                        </button>
                                    ))}
                                </span>
                            </span>
                            </>)}
                        </span>

                        {!noNumbers && (
                        <span className='mt-6 block'>
                            <Label id='meal-activity'>How active is a typical day?</Label>
                            <span role='group' aria-labelledby='meal-activity' className='mt-2 grid gap-2 sm:grid-cols-3'>
                                {ACTIVITY_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        aria-pressed={form.activity === option.value}
                                        onClick={() => update('activity', option.value)}
                                        className={choiceClass(form.activity === option.value)}
                                    >
                                        <span className='text-sm font-semibold'>{option.label}</span>
                                        <span className={cn('mt-0.5 text-xs', form.activity === option.value ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                                            {option.hint}
                                        </span>
                                    </button>
                                ))}
                            </span>
                        </span>
                        )}

                        {underage && (
                            <span role='alert' className='mt-4 block rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-foreground'>
                                This planner is built for adults aged 18 and over. Children and teenagers are still growing and
                                have different needs, so it will not build a plan for this age.
                            </span>
                        )}
                        {attempted && !underage && !aboutYouValid && (
                            <span role='alert' className='mt-4 block text-sm font-medium text-destructive'>
                                {!ageValid ? 'Enter an age between 18 and 100. ' : ''}
                                {!noNumbers && !weightValid ? 'Enter your weight. ' : ''}
                                {!noNumbers && !heightValid ? 'Enter your height.' : ''}
                            </span>
                        )}

                        <span className='mt-6 flex justify-between gap-3'>
                            <button type='button' onClick={() => go(1)} className={secondaryButton}>
                                <ArrowLeft className='size-4' />
                                Back
                            </button>
                            <button
                                type='button'
                                onClick={() => (aboutYouValid ? go(3) : setAttempted(true))}
                                disabled={underage}
                                className={primaryButton}
                            >
                                Next: your food
                                <ArrowRight className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {showForm && step === 3 && (
                    <div className='mt-6'>
                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-xl font-semibold tracking-tight text-foreground outline-none'>
                            Your food
                        </span>

                        {!noNumbers && (
                            <span className='mt-5 block'>
                                <Label id='meal-goal'>Main goal</Label>
                                <span role='group' aria-labelledby='meal-goal' className='mt-2 grid gap-2 sm:grid-cols-3'>
                                    {GOAL_OPTIONS.map((option) => {
                                        const disabled = option.value === 'lose' && loseBlocked
                                        const selected = goal === option.value
                                        return (
                                            <button
                                                key={option.value}
                                                type='button'
                                                aria-pressed={selected}
                                                disabled={disabled}
                                                onClick={() => chooseGoal(option.value)}
                                                className={choiceClass(selected, disabled)}
                                            >
                                                <span className='text-sm font-semibold'>{option.label}</span>
                                                <span className={cn('mt-0.5 text-xs', selected ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                                                    {option.hint}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </span>
                                {loseBlocked && (
                                    <span className='mt-2 block text-xs leading-relaxed text-muted-foreground'>
                                        {underweight
                                            ? `Weight loss is not offered because your BMI is under ${UNDERWEIGHT_BMI}, which the World Health Organization classes as underweight.`
                                            : 'Weight loss is not offered because of your health answers. Your doctor can tell you whether it is right for you.'}
                                    </span>
                                )}
                            </span>
                        )}

                        <span className={cn('block', noNumbers ? 'mt-5' : 'mt-6')}>
                            <Label id='meal-pattern'>How do you eat?</Label>
                            <span role='group' aria-labelledby='meal-pattern' className='mt-2 flex flex-wrap gap-2'>
                                {PATTERN_OPTIONS.map((option) => (
                                    <button key={option.value} type='button' aria-pressed={form.pattern === option.value} onClick={() => update('pattern', option.value)} className={chipClass(form.pattern === option.value)}>
                                        {option.label}
                                    </button>
                                ))}
                            </span>
                        </span>

                        <span className='mt-6 block'>
                            <Label id='meal-avoid'>Foods to leave out (optional)</Label>
                            <span role='group' aria-labelledby='meal-avoid' className='mt-2 flex flex-wrap gap-2'>
                                {visibleAvoid.map((option) => (
                                    <button key={option.value} type='button' aria-pressed={avoid.includes(option.value)} onClick={() => toggleAvoid(option.value)} className={chipClass(avoid.includes(option.value))}>
                                        {option.label}
                                    </button>
                                ))}
                            </span>
                            <span className='mt-2 block text-xs leading-relaxed text-muted-foreground'>
                                Meals with these foods are left out. With an allergy, still check every label: the plan cannot
                                know about traces in packaged foods.
                            </span>
                        </span>

                        <span className='mt-6 grid gap-6 sm:grid-cols-2'>
                            <span className='block'>
                                <Label id='meal-snacks'>Snacks a day</Label>
                                <span role='group' aria-labelledby='meal-snacks' className='mt-2 flex flex-wrap gap-2'>
                                    {[0, 1, 2].map((value) => (
                                        <button key={value} type='button' aria-pressed={form.snacks === value} onClick={() => update('snacks', value)} className={chipClass(form.snacks === value)}>
                                            {value === 0 ? 'None' : value}
                                        </button>
                                    ))}
                                </span>
                            </span>
                            <span className='block'>
                                <Label id='meal-time'>Cooking time</Label>
                                <span role='group' aria-labelledby='meal-time' className='mt-2 flex flex-wrap gap-2'>
                                    <button type='button' aria-pressed={!form.quick} onClick={() => update('quick', false)} className={chipClass(!form.quick)}>
                                        Any
                                    </button>
                                    <button type='button' aria-pressed={form.quick} onClick={() => update('quick', true)} className={chipClass(form.quick)}>
                                        Mostly quick meals
                                    </button>
                                </span>
                            </span>
                        </span>

                        <span className='mt-6 flex justify-between gap-3'>
                            <button type='button' onClick={() => go(2)} className={secondaryButton}>
                                <ArrowLeft className='size-4' />
                                Back
                            </button>
                            <button type='button' onClick={submit} className={primaryButton}>
                                Build my plan
                                <Check className='size-4' />
                            </button>
                        </span>
                    </div>
                )}

                {pending && (
                    <div role='status' aria-live='polite' className='flex flex-col items-center py-8 text-center'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-primary/10'>
                            <LoaderCircle className='size-6 text-primary motion-safe:animate-spin' />
                        </span>
                        <span className='mt-4 block font-heading text-xl font-semibold tracking-tight text-foreground'>
                            Preparing your plan
                        </span>
                        <span className='mt-6 flex w-full max-w-sm flex-col gap-3 text-left'>
                            {PREP_STEPS.map((label, index) => {
                                const done = index < prepStep
                                const active = index === prepStep
                                return (
                                    <span key={label} className={cn('flex items-center gap-3 text-sm transition-opacity duration-300', done || active ? 'opacity-100' : 'opacity-40')}>
                                        <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-full border', done ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background')}>
                                            {done ? <Check className='size-4' /> : active ? <LoaderCircle className='size-4 text-primary motion-safe:animate-spin' /> : null}
                                        </span>
                                        <span className={cn(done || active ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
                                    </span>
                                )
                            })}
                        </span>
                    </div>
                )}

                {plan && (
                    <div data-print-area ref={printAreaRef}>
                        <span className='mb-6 hidden rounded-xl border border-border px-4 py-3 text-sm font-semibold text-foreground print:block'>
                            Made with the free meal plan builder from WellPath Fusion
                        </span>

                        <span ref={headingRef} tabIndex={-1} className='block font-heading text-2xl font-semibold tracking-tight text-foreground outline-none'>
                            Your 7-day meal plan
                        </span>

                        {targets && (
                            <span className='mt-4 grid gap-2 sm:grid-cols-2'>
                                <span className='block rounded-xl border border-primary/40 bg-primary/5 px-4 py-3'>
                                    <span className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
                                        <Flame className='size-4 text-primary' />
                                        Energy a day
                                    </span>
                                    <span className='mt-1 block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                        {formatNumber(targets.kcal[0])} to {formatNumber(targets.kcal[1])} kcal
                                    </span>
                                    <span className='mt-1 block text-xs leading-relaxed text-muted-foreground'>
                                        {plan.lowered
                                            ? 'About 500 kcal a day below what you burn, the standard gentle approach.'
                                            : 'About what you burn, for a steady weight.'}
                                    </span>
                                </span>
                                <span className='block rounded-xl border border-primary/40 bg-primary/5 px-4 py-3'>
                                    <span className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
                                        <Utensils className='size-4 text-primary' />
                                        Protein a day
                                    </span>
                                    {targets.protein ? (
                                        <>
                                            <span className='mt-1 block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                                {targets.protein[0]} to {targets.protein[1]} g
                                            </span>
                                            <span className='mt-1 block text-xs leading-relaxed text-muted-foreground'>
                                                {targets.perMeal && `About ${targets.perMeal[0]} to ${targets.perMeal[1]} g at each main meal. `}
                                                {targets.proteinSource === 'issn'
                                                    ? 'The sports nutrition range for people who train.'
                                                    : 'The range in the 2025 to 2030 US dietary guidelines.'}
                                            </span>
                                        </>
                                    ) : (
                                        <span className='mt-1 block text-sm leading-relaxed text-foreground'>
                                            Ask your doctor or dietitian. Protein needs change with some health conditions,
                                            including kidney disease.
                                        </span>
                                    )}
                                </span>
                            </span>
                        )}

                        {targets && (
                            <span className='mt-2 grid gap-2 sm:grid-cols-2'>
                                <span className='flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                    <Leaf className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span>At least {targets.fruitVeg} g of fruit and vegetables</span>
                                </span>
                                <span className='flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                    <Wheat className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span>At least {targets.fibre} g of fiber</span>
                                </span>
                                <span className='flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                    <Info className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span>Free sugars under {targets.sugar[1]} g, ideally under {targets.sugar[0]} g</span>
                                </span>
                                <span className='flex items-start gap-2 rounded-xl bg-secondary/50 px-3 py-2.5 text-sm text-foreground'>
                                    <Info className='mt-0.5 size-4 shrink-0 text-primary' />
                                    <span>Salt under {targets.salt} g, saturated fat under {targets.satFat} g</span>
                                </span>
                            </span>
                        )}

                        {plan.notes.length > 0 && (
                            <span className='mt-5 flex flex-col gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                {plan.notes.map((note) => (
                                    <span key={note} className='flex gap-2 text-sm leading-relaxed text-foreground'>
                                        <TriangleAlert className='mt-0.5 size-4 shrink-0 text-primary' />
                                        {note}
                                    </span>
                                ))}
                            </span>
                        )}

                        <span className='mt-5 block rounded-xl border border-border bg-background px-4 py-3 text-sm leading-relaxed text-foreground'>
                            <span className='font-semibold'>Every meal is built on the same plate:</span> half vegetables or
                            fruit, a quarter protein, a quarter whole grains or starchy vegetables, and a little healthy fat
                            such as olive oil.{' '}
                            {plan.targets && plan.lowered && 'To eat a little less, keep the vegetables generous and trim the starchy quarter first. '}
                            <Link href='/nutrition/anti-inflammatory-plate' className='font-medium text-primary underline underline-offset-2 print:hidden'>
                                How the plate works
                            </Link>
                        </span>

                        <span className='mt-6 block text-sm font-semibold text-foreground print:hidden'>Your week</span>
                        <span role='tablist' aria-label='Day of the week' className='mt-3 flex gap-1.5 overflow-x-auto pb-1 print:hidden'>
                            {plan.days.map((entry, index) => (
                                <button
                                    key={entry.day}
                                    type='button'
                                    role='tab'
                                    id={`meal-day-tab-${index}`}
                                    aria-selected={dayIndex === index}
                                    aria-controls={`meal-day-${index}`}
                                    onClick={() => setDayIndex(index)}
                                    className={cn(
                                        'min-h-11 shrink-0 rounded-full border px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        dayIndex === index ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-secondary'
                                    )}
                                >
                                    {entry.day.slice(0, 3)}
                                </button>
                            ))}
                        </span>

                        <span className='mt-3 block'>
                            {plan.days.map((entry, index) => (
                                <span
                                    key={entry.day}
                                    id={`meal-day-${index}`}
                                    role='tabpanel'
                                    aria-labelledby={`meal-day-tab-${index}`}
                                    className={cn(
                                        'break-inside-avoid rounded-xl border border-border bg-background px-4 py-4 print:mb-3 print:block',
                                        dayIndex === index ? 'block' : 'hidden'
                                    )}
                                >
                                    <span className='block font-heading text-base font-semibold text-foreground'>{entry.day}</span>
                                    <span className='mt-3 flex flex-col gap-2'>
                                        {plan.keys.map((key) => {
                                            const meal = mealById(entry.meals[key])
                                            if (!meal) return null
                                            const taken = Object.entries(entry.meals).filter(([other]) => other !== key).map(([, id]) => id as string)
                                            const canSwap = nextOption(meal.id, SLOT_OF[key], { pattern: form.pattern, avoid, quick: form.quick }, taken) !== null

                                            return (
                                                <span key={key} className='flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-2.5 print:border-0 print:px-0 print:py-1'>
                                                    <span className='w-20 shrink-0 pt-0.5 text-xs font-semibold uppercase tracking-wide text-primary'>
                                                        {MEAL_LABEL[key]}
                                                    </span>
                                                    <span className='min-w-0 flex-1'>
                                                        <span className='block text-sm font-semibold text-foreground'>
                                                            {mealText(meal.name, form.pattern, avoid)}
                                                        </span>
                                                        <span className='mt-0.5 block text-sm leading-relaxed text-muted-foreground'>
                                                            {mealText(meal.description, form.pattern, avoid)}
                                                        </span>
                                                        {meal.quick && (
                                                            <span className='mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground'>
                                                                <Clock className='size-3.5' />
                                                                Quick
                                                            </span>
                                                        )}
                                                    </span>
                                                    {canSwap && (
                                                        <button
                                                            type='button'
                                                            onClick={() => swap(index, key)}
                                                            aria-label={`Swap ${entry.day} ${MEAL_LABEL[key].toLowerCase()}`}
                                                            className='inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:hidden'
                                                        >
                                                            <RefreshCw className='size-4' />
                                                            <span className='hidden sm:inline'>Swap</span>
                                                        </button>
                                                    )}
                                                </span>
                                            )
                                        })}
                                    </span>
                                </span>
                            ))}
                        </span>
                        <span className='mt-2 block text-xs text-muted-foreground print:hidden'>
                            Tap a day to see its meals. Swap any meal you do not fancy for another that fits your answers.
                            Leftovers from dinner make an easy next-day lunch.
                        </span>

                        <span className='mt-6 block break-inside-avoid rounded-xl border border-border bg-background px-4 py-4 print:break-before-page'>
                            <span className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                                <ShoppingBasket className='size-4 text-primary' />
                                Shopping list for the week
                            </span>
                            <span className='mt-1 block text-xs leading-relaxed text-muted-foreground'>
                                The number shows how many meals use each item, to help you judge how much to buy. Herbs, oil
                                and spices you already have can be skipped.
                            </span>
                            <span className='mt-4 grid gap-5 sm:grid-cols-2'>
                                {groups.map((group) => (
                                    <span key={group.aisle} className='block break-inside-avoid'>
                                        <span className='block text-xs font-semibold uppercase tracking-wide text-primary'>{group.aisle}</span>
                                        <span className='mt-2 flex flex-col gap-1.5'>
                                            {group.items.map((item) => (
                                                <span key={item.name} className='flex items-center gap-2 text-sm text-foreground'>
                                                    <span aria-hidden='true' className='size-3.5 shrink-0 rounded-sm border border-border' />
                                                    <span className='min-w-0 flex-1'>{item.name}</span>
                                                    <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
                                                        {item.meals} {item.meals === 1 ? 'meal' : 'meals'}
                                                    </span>
                                                </span>
                                            ))}
                                        </span>
                                    </span>
                                ))}
                            </span>
                        </span>

                        <span className='mt-6 hidden text-xs leading-relaxed text-muted-foreground print:block'>
                            A general plan for healthy adults, not medical or dietetic advice. With a health condition, an
                            allergy, pregnancy or medicines, check with your doctor or a registered dietitian.
                        </span>

                        <span className='mt-6 flex flex-wrap gap-3 print:hidden'>
                            <button type='button' onClick={print} className={cn(primaryButton, 'px-5 py-2.5')}>
                                <Printer className='size-4' />
                                Print or save as PDF
                            </button>
                            <button
                                type='button'
                                onClick={() => {
                                    interacted.current = true
                                    setPlan(null)
                                    setStep(3)
                                }}
                                className={cn(secondaryButton, 'py-2.5')}
                            >
                                <ArrowLeft className='size-4' />
                                Change answers
                            </button>
                            <button type='button' onClick={restart} className={cn(secondaryButton, 'py-2.5')}>
                                <RotateCcw className='size-4' />
                                Start again
                            </button>
                        </span>
                    </div>
                )}

                <div className='mt-6 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3 print:hidden'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        A general plan for healthy adults, not medical or dietetic advice. Your answers are not sent to us or
                        stored by us. A finished plan is kept in this browser until you start again.
                    </span>
                </div>
            </div>
        </section>
    )
}

export default MealPlanner
